import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@utilities/icon';
import { colors } from '@configs/Theme';
import Divider from '@com/_atoms/Divider';
import { DoctorReviewItem } from '@models/ocs/doctor';
import { Button } from '@com/_atoms/NewButton';
import { convertToPersianDate } from '@utilities/convertToPersianDate';

interface CommentsProps {
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  dataPages: DoctorReviewItem[][];
  totalReviewsCount?: number;
}

const Comments = ({
  dataPages,
  totalReviewsCount,
  fetchNextPage,
  hasNextPage,
}: CommentsProps) => {
  const commentsList = dataPages.flat();

  return (
    <>
      <div className="flex flex-col gap-y-4 px-4 pb-4 select-none">
        <div className="w-full">
          <span className="text-Label/Medium">
            نظر کاربران ({totalReviewsCount})
          </span>
        </div>

        <AnimatePresence>
          {commentsList.map((item, index) => (
            <motion.div
              key={`${item.id ?? index}-${item.createdAt}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col gap-y-2"
            >
              {index !== 0 && <Divider />}
              <div className="flex gap-y-1 justify-between">
                <div className="text-Body/Small text-content-primary font-semibold ltr">
                  کاربر تپسی دکتر
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-Body/xSmall text-content-tertiary ml-4">
                    {convertToPersianDate(item.createdAt, {
                      format: 'dd MM yyyy',
                    })}
                  </div>
                  <div className="flex flex-row-reverse justify-center items-center text-Body/Small text-content-secondary">
                    <span>{item.rate}</span>
                    <Icon
                      name="StarFill"
                      width={0.9}
                      height={0.9}
                      fill={colors.yellow[400]}
                    />
                  </div>
                </div>
              </div>

              <div
                className="text-Body/Small text-content-tertiary"
                style={{ whiteSpace: 'pre-line' }}
              >
                {item.comment}
              </div>

              {item.satisfactionTypeName &&
              item?.satisfactionTypeName?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {item.satisfactionTypeName.map((type, index) => (
                    <span
                      key={`${type || 'tag'}-${index}`}
                      className="text-Label/xSmall px-2 py-1 rounded-full border border-border-primary"
                    >
                      {type || ''}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 mt-2">
                  {item.dissatisfactionTypeName &&
                    item.dissatisfactionTypeName.map((type, index) => (
                      <span
                        key={`${type || 'tag'}-${index}`}
                        className="text-Label/xSmall px-2 py-1 rounded-full border border-border-primary"
                      >
                        {type || ''}
                      </span>
                    ))}
                </div>
              )}
              {!!item?.referralReasons?.length && (
                <div className="flex bg-surface-disable w-full rounded-lg items-center px-3 py-0.5 my-2">
                  <p className="text-Body/xSmall pl-1">علت مشاوره: </p>
                  {item?.referralReasons?.map((referralReason, index) => (
                    <span key={referralReason.id} className="text-Label/xSmall">
                      {referralReason.name || ''}
                      <span className="px-1">
                        {index < item?.referralReasons?.length - 1 && '-'}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="secondary"
              size="small"
              onClick={fetchNextPage}
              icon={<Icon name="ChevronDown" width={1.25} height={1.25} />}
            >
              مشاهده بیشتر
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default Comments;
